import { FormEvent, useEffect, useState } from 'react';
import type { AdminBillingPayment, AdminBillingRow, AdminBillingStatus, AdminPaymentClaim, AdminPaymentMethod } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';
import { normMoney } from './Packages';

// Maliyyə (KT-72 hissə 2): aylıq hesablar (obyekt × ay), ödənişi qeyd et / ləğv et (əks yazı), ödəniş bəyanları.
// Məbləğlər serverdən onluq sətir kimi gəlir; ekranda yalnız formatlanır, hesablanmır.

const STATUS: Record<AdminBillingStatus, string> = { open: 'Açıq', awaiting: 'Ödəniş gözlənilir', paid: 'Ödənildi', overdue: 'Gecikir' };
const PLAN: Record<string, string> = { simple: 'Sadə', standard: 'Standart', full: 'Tam', custom: 'Özün qur' };
const METHOD: Record<AdminPaymentMethod, string> = { bank: 'Bank köçürməsi', cash: 'Nağd', card: 'Kart', other: 'Digər' };

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

export function money(v: string): string {
  const neg = v.startsWith('-');
  const [i, f = '00'] = (neg ? v.slice(1) : v).split('.');
  return `${neg ? '−' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${(f + '00').slice(0, 2)} ₼`;
}

function bakuMonth(offset = 0): string {
  const now = new Date(Date.now() + 4 * 3600_000); // Bakı UTC+4
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function bakuToday(): string {
  return new Date(Date.now() + 4 * 3600_000).toISOString().slice(0, 10);
}

export function Billing({ isOwner }: { isOwner: boolean }) {
  const [tab, setTab] = useState<'months' | 'claims'>('months');
  return (
    <>
      <section className="card card-wide">
        <div className="toolbar">
          <h2>Maliyyə</h2>
          <span className="topbar-spacer" />
          <div className="tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'months'} className={tab === 'months' ? 'tab active' : 'tab'} onClick={() => setTab('months')}>
              Aylıq hesablar
            </button>
            <button type="button" role="tab" aria-selected={tab === 'claims'} className={tab === 'claims' ? 'tab active' : 'tab'} onClick={() => setTab('claims')}>
              Ödəniş bəyanları
            </button>
          </div>
        </div>
        {tab === 'months' ? <Months isOwner={isOwner} /> : <Claims />}
      </section>
    </>
  );
}

function Months({ isOwner }: { isOwner: boolean }) {
  const [month, setMonth] = useState(bakuMonth());
  const [rows, setRows] = useState<AdminBillingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function load(m = month) {
    setRows(null);
    setError(null);
    adminApi
      .billing(m)
      .then(setRows)
      .catch((err) => setError(errText(err)));
  }
  useEffect(() => load(month), [month]);

  async function act(fn: () => Promise<{ rows: number }>, done: (n: number) => string) {
    setBusy(true);
    setNotice(null);
    try {
      setNotice(done((await fn()).rows));
      load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  const past = month < bakuMonth();
  const anyOpen = rows?.some((r) => r.closedAt === null) ?? false;

  return (
    <>
      <div className="toolbar">
        <label className="field inline-field">
          Ay
          <input type="month" lang="az" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </label>
        <span className="topbar-spacer" />
        {isOwner && month === bakuMonth() && (
          <button type="button" className="btn-small" disabled={busy} onClick={() => act(() => adminApi.billingRefresh(month), (n) => `Yenidən hesablandı: ${n} obyekt.`)}>
            Yenidən hesabla
          </button>
        )}
        {isOwner && past && anyOpen && (
          <button type="button" className="btn-small" disabled={busy} onClick={() => act(() => adminApi.billingClose(month), (n) => `Ay bağlandı: ${n} hesab.`)}>
            Ayı bağla
          </button>
        )}
      </div>
      <p className="muted small">Açıq ay hər saat yenidən hesablanır; ay bitəndə avtomatik bağlanır, ödəniş müddəti bağlanmadan 7 gün. Sınaq və dondurulmuş abunə 0 ₼.</p>
      {notice && <p className="ok" role="status">{notice}</p>}
      {error && <p className="bad" role="alert">{error}</p>}
      {!rows && !error && <p className="muted">Yüklənir…</p>}
      {rows && rows.length === 0 && <p className="muted">Bu ay üçün hesab yoxdur.</p>}
      {rows && rows.length > 0 && (
        <div className="table-scroll">
          <table className="plain">
            <thead>
              <tr>
                <th>Obyekt</th>
                <th>Paket</th>
                <th className="num narrow-hide">Abunə</th>
                <th className="num narrow-hide">Əlavə</th>
                <th className="num narrow-hide">AI</th>
                <th className="num narrow-hide">Endirim</th>
                <th className="num">Yekun</th>
                <th className="num">Ödənilib</th>
                <th className="num">Qalıq</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <BillingLine key={r.id} r={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} onChanged={() => load()} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function BillingLine({ r, open, onToggle, onChanged }: { r: AdminBillingRow; open: boolean; onToggle: () => void; onChanged: () => void }) {
  return (
    <>
      <tr>
        <td>
          <strong>{r.tenantName}</strong>
          <div className="muted small">{r.outletName}</div>
        </td>
        <td className="small">
          {r.planCode ? (PLAN[r.planCode] ?? r.planCode) : '—'}
          {r.subStatus && r.subStatus !== 'active' && <div className="muted">{r.subStatus === 'trial' ? 'sınaq' : r.subStatus === 'frozen' ? 'dondurulub' : r.subStatus}</div>}
        </td>
        <td className="num nowrap narrow-hide">{money(r.planAmount)}</td>
        <td className="num nowrap narrow-hide">{money(r.addonsAmount)}</td>
        <td className="num nowrap narrow-hide">{money(r.aiAmount)}</td>
        <td className="num nowrap narrow-hide">{money(r.discount)}</td>
        <td className="num nowrap">
          <strong>{money(r.total)}</strong>
        </td>
        <td className="num nowrap">{money(r.paid)}</td>
        <td className="num nowrap">{money(r.balance)}</td>
        <td>
          <span className={`tag nowrap status-${r.status}`}>{STATUS[r.status]}</span>
          {r.dueOn && <div className="muted small">{r.dueOn}-dək</div>}
        </td>
        <td>
          <button type="button" className="btn-small" onClick={onToggle} aria-expanded={open}>
            Ödənişlər
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={11}>
            <Payments billing={r} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function Payments({ billing, onChanged }: { billing: AdminBillingRow; onChanged: () => void }) {
  const [rows, setRows] = useState<AdminBillingPayment[] | null>(null);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(bakuToday());
  const [method, setMethod] = useState<AdminPaymentMethod>('bank');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function load() {
    adminApi
      .billingPayments(billing.id)
      .then(setRows)
      .catch((err) => setError(errText(err)));
  }
  useEffect(load, [billing.id]);

  async function add(e: FormEvent) {
    e.preventDefault();
    const a = normMoney(amount);
    if (!a || a === '0.00') return setError('Məbləğ «120,00» formasında');
    setBusy(true);
    setError(null);
    try {
      await adminApi.billingPay(billing.id, { amount: a, paidOn, method, note: note || undefined });
      setAmount('');
      setNote('');
      load();
      onChanged();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function reverse(p: AdminBillingPayment) {
    const reason = window.prompt('Ləğvin səbəbi (ödəniş silinmir — əks yazı yaranır):');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await adminApi.paymentReverse(p.id, reason.trim());
      load();
      onChanged();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lock-panel">
      {rows && rows.length > 0 && (
        <ul className="payment-list">
          {rows.map((p) => (
            <li key={p.id}>
              <span className="nowrap">{p.paidOn}</span> · <strong className="nowrap">{money(p.amount)}</strong> · {METHOD[p.method]}
              {p.note && <span className="muted"> — {p.note}</span>}
              <span className="muted small"> ({p.createdBy})</span>
              {p.reverses && <span className="tag">ləğv yazısı</span>}
              {p.reversed && <span className="tag">ləğv olunub</span>}
              {!p.reverses && !p.reversed && (
                <button type="button" className="link-btn dark small" onClick={() => reverse(p)} disabled={busy}>
                  Ləğv et
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {rows && rows.length === 0 && <p className="muted small">Ödəniş yoxdur.</p>}
      <form className="limit-grid" onSubmit={add} noValidate>
        <label className="field">
          Məbləğ, ₼
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={billing.balance.replace('.', ',')} disabled={busy} />
        </label>
        <label className="field">
          Tarix
          <input type="date" lang="az" value={paidOn} max={bakuToday()} onChange={(e) => setPaidOn(e.target.value)} disabled={busy} />
        </label>
        <label className="field">
          Üsul
          <select value={method} onChange={(e) => setMethod(e.target.value as AdminPaymentMethod)} disabled={busy}>
            {(Object.keys(METHOD) as AdminPaymentMethod[]).map((m) => (
              <option key={m} value={m}>
                {METHOD[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Qeyd
          <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} disabled={busy} />
        </label>
        <div className="form-actions">
          <button type="submit" className="btn-primary btn-small" disabled={busy || !amount}>
            Ödənişi qeyd et
          </button>
        </div>
      </form>
      {error && <p className="bad" role="alert">{error}</p>}
    </div>
  );
}

function Claims() {
  const [rows, setRows] = useState<AdminPaymentClaim[] | null>(null);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function load() {
    adminApi
      .claims(all ? 'all' : 'pending')
      .then(setRows)
      .catch((err) => setError(errText(err)));
  }
  useEffect(load, [all]);

  async function decide(c: AdminPaymentClaim, approve: boolean) {
    const note = approve ? undefined : window.prompt('Rədd səbəbi:') ?? '';
    if (!approve && !note?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.claimDecide(c.id, approve, note?.trim());
      load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <label className="check">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Baxılmışları da göstər
      </label>
      {error && <p className="bad" role="alert">{error}</p>}
      {!rows && !error && <p className="muted">Yüklənir…</p>}
      {rows && rows.length === 0 && <p className="muted">Yoxlanılacaq bəyan yoxdur.</p>}
      {rows && rows.length > 0 && (
        <div className="table-scroll">
          <table className="plain">
            <thead>
              <tr>
                <th>Şirkət</th>
                <th>Hesab ayı</th>
                <th className="num">Məbləğ</th>
                <th>Tarix · üsul</th>
                <th>İstinad</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.tenantName}</td>
                  <td>{c.billingMonth?.slice(0, 7) ?? '—'}</td>
                  <td className="num nowrap">{money(c.amount)}</td>
                  <td className="small">
                    {c.paidOn} · {METHOD[c.method]}
                  </td>
                  <td className="small">{c.reference ?? '—'}</td>
                  <td>
                    {c.status === 'pending' ? 'Yoxlanılır' : c.status === 'approved' ? 'Təsdiqləndi' : 'Rədd edildi'}
                    {c.decisionNote && <div className="muted small">{c.decisionNote}</div>}
                  </td>
                  <td className="nowrap">
                    {c.status === 'pending' && (
                      <>
                        <button type="button" className="btn-small" disabled={busy || !c.billingId} title={c.billingId ? '' : 'Hesab ayı seçilməyib'} onClick={() => decide(c, true)}>
                          Təsdiq
                        </button>{' '}
                        <button type="button" className="btn-small" disabled={busy} onClick={() => decide(c, false)}>
                          Rədd
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
